import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import PropsTable from './PropsTable';
import CodeBlock from './CodeBlock';

/**
 * ComponentPage — the uniform scaffold every documentation page renders inside.
 *
 * Title → description → demos → Props → Usage, in that order, on every page.
 * Pages supply their demos as children and everything else through `meta`, so
 * no page re-invents the layout.
 */

/**
 * `meta.props` (one set) and `meta.propSets` (several) normalize to one shape.
 *
 * `componentName` is the EXPORT name and may differ from the page title —
 * "File Upload" documents `FileUploadSimple`. The drift test reports by this
 * name, so it has to win over the title.
 */
export const toPropSets = (meta = {}) => {
  if (Array.isArray(meta.propSets)) return meta.propSets;
  if (Array.isArray(meta.props)) {
    return [{
      component: meta.component,
      componentName: meta.componentName || meta.title,
      rows: meta.props,
      ignore: meta.ignore,
    }];
  }
  return [];
};

/**
 * The jump row is read out of the DOM rather than declared in `meta`.
 * Demos are opaque children, so the alternative was listing every card title
 * in metadata — duplication that would drift the first time a demo was
 * renamed. Querying keeps it correct for free.
 */
const useSectionLinks = (rootRef, deps) => {
  const [links, setLinks] = useState([]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const found = [...root.querySelectorAll('.ds-page__card-head[id]')].map((head) => ({
      id: head.id,
      label: head.querySelector('.ds-page__card-title')?.textContent ?? head.id,
    }));
    setLinks(found);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return links;
};

const ComponentPage = ({ meta, children }) => {
  const propSets = toPropSets(meta);
  const usage = meta.usage || [];
  const rootRef = useRef(null);
  const { pathname } = useLocation();
  const sections = useSectionLinks(rootRef, [pathname]);

  // Only worth showing when there is actually something to skip past.
  const showJump = sections.length >= 3;

  return (
    <article ref={rootRef}>
      <header className="ds-page__header">
        {meta.group && <span className="typo-body-sm ds-page__eyebrow">{meta.group}</span>}
        <h1 className="typo-h2 ds-page__title">{meta.title}</h1>
        {meta.description && (
          <p className="typo-body-lg ds-page__description">{meta.description}</p>
        )}
        {showJump && (
          <nav className="ds-page__jump" aria-label="On this page">
            <span className="typo-body-sm ds-page__jump-label">On this page</span>
            {sections.map((section) => (
              <a key={section.id} href={`#${section.id}`} className="typo-body ds-page__jump-link">
                {section.label}
              </a>
            ))}
            {propSets.length > 0 && (
              <a href="#props" className="typo-body ds-page__jump-link">Props</a>
            )}
            {usage.length > 0 && (
              <a href="#usage" className="typo-body ds-page__jump-link">Usage</a>
            )}
          </nav>
        )}

        {(meta.importPath || meta.figmaNode) && (
          <div className="ds-page__meta">
            {meta.importPath && (
              <span className="typo-body-sm ds-page__chip">
                import <code>{meta.importPath}</code>
              </span>
            )}
            {meta.figmaNode && (
              <span className="typo-body-sm ds-page__chip">
                Figma <code>{meta.figmaNode}</code>
              </span>
            )}
          </div>
        )}
      </header>

      {children}

      {propSets.length > 0 && (
        <section className="ds-page__section" id="props">
          <h2 className="typo-h3 ds-page__section-title">Props</h2>
          {propSets.map((set, i) => (
            <div key={set.title || set.componentName || i} style={i > 0 ? { paddingTop: 'var(--space-ui-medium-m)' } : undefined}>
              <PropsTable
                title={propSets.length > 1 ? (set.title || set.componentName) : undefined}
                rows={set.rows}
                component={set.component ? (set.componentName || meta.title) : undefined}
              />
            </div>
          ))}
          {meta.propsNote && (
            <p
              className="typo-body"
              style={{ paddingTop: 'var(--space-ui-medium-s)', color: 'var(--ds-doc-ink)', maxWidth: '70ch' }}
            >
              {meta.propsNote}
            </p>
          )}
        </section>
      )}

      {usage.length > 0 && (
        <section className="ds-page__section" id="usage">
          <h2 className="typo-h3 ds-page__section-title">Usage</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-ui-medium-m)' }}>
            {usage.map((block, i) => (
              <CodeBlock key={block.title || i} title={block.title} code={block.code} />
            ))}
          </div>
        </section>
      )}
    </article>
  );
};

ComponentPage.propTypes = {
  /** Page metadata — title, description, props/propSets, usage */
  meta: PropTypes.object.isRequired,
  /** The page's live demos */
  children: PropTypes.node,
};

export default ComponentPage;
